import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import { stats, countries } from "../data/mock";
import { api } from "../lib/api";
import {
  ArrowRight, Star, CheckCircle2, Globe, Shield, Users, Award,
  Briefcase, GraduationCap, Home, Plane, TrendingUp, BookOpen, Languages, MessageCircle, ChevronRight } from
"lucide-react";

const iconMap = {
  Briefcase, GraduationCap, Home, Plane, TrendingUp, BookOpen, Languages, MessageCircle
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.getServices().then(setServices).catch(console.error);
    api.getTestimonials().then(setTestimonials).catch(console.error);
  }, []);
  return (
    <div className="overflow-hidden">
      <SEO 
        title="Your Gateway to Global Opportunities"
        description="Expert immigration consultancy for work permits, study visas, permanent residency, and more. Trusted by 500+ successful applicants. Book your free consultation today."
        keywords="immigration consultancy, visa services, work permit, study visa, PR, permanent residency, IELTS coaching, German language, Coimbatore immigration consultant"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[55%] h-full bg-gradient-to-bl from-sky-50 via-brand-50/20 to-transparent rounded-bl-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-xl">
              <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold text-gray-900 leading-[1.1] mb-6">
                Your Gateway to
                <span className="text-brand-500 block mt-2">Global Opportunities</span>
              </h1>
              <p className="text-lg text-gray-500 leading-relaxed mb-10 max-w-md">
                Expert immigration consultancy for work permits, study visas, PR, and more. We make your international dreams a reality.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl h-12 px-7 text-sm font-semibold shadow-lg shadow-brand-200/60 hover:shadow-brand-300/60 transition-all duration-300 hover:-translate-y-0.5">
                    Book Free Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="border-2 border-brand-200 text-brand-700 hover:bg-brand-50 hover:border-brand-300 rounded-xl h-12 px-7 text-sm font-semibold transition-all duration-300">
                    Learn More
                  </Button>
                </Link>
              </div>

              {/* Countries */}
              <div className="mt-12 flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">Popular Destinations:</span>
                {countries.slice(0, 5).map((country) =>
                <Badge key={country} variant="outline" className="border-gray-200 text-gray-500 text-xs font-medium rounded-lg">
                    {country}
                  </Badge>
                )}
              </div>
            </div>

            {/* Hero Image */}
            <div className="hidden lg:block relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-brand-200/40">
                <img
                  src="https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=900&q=80"
                  alt="Airplane flying above clouds"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-800/20 to-transparent" />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-brand-100/60">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-brand-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">98%</p>
                    <p className="text-xs text-gray-500">Success Rate</p>
                  </div>
                </div>
              </div>
              {/* Floating Countries Card */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-brand-100/60">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-brand-500" />
                  <span className="text-sm font-semibold text-gray-800">15+ Countries</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-brand-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <div key={index} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-brand-200 text-sm font-medium">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Our Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Immigration Solutions
            </h2>
            <p className="text-gray-500 text-lg">
              From visa applications to language coaching, we offer end-to-end support for your international journey.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Globe;
              return (
                <Link key={service.slug} to={`/services/${service.slug}`}>
                  <Card className="group border-0 shadow-sm hover:shadow-xl hover:shadow-brand-100/50 transition-all duration-300 hover:-translate-y-1 bg-white rounded-2xl h-full cursor-pointer">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-brand-500 transition-colors duration-300">
                        <IconComponent className="w-6 h-6 text-brand-500 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-base mb-2 group-hover:text-brand-700 transition-colors">{service.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4">{service.shortDescription}</p>
                      <span className="text-brand-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Learn More <ChevronRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>);

            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
                Why NexFLY
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Thousands Trust Us With Their Immigration Dreams
              </h2>
              <p className="text-gray-500 text-lg mb-10 leading-relaxed">With over 3 years of experience and a 98% success rate, NexFLY has been the preferred choice for individuals and families seeking to explore the world.

              </p>

              <div className="space-y-6">
                {[
                { icon: Shield, title: "Licensed & Certified Consultants", desc: "Our team comprises government-licensed immigration consultants with verified credentials." },
                { icon: Users, title: "Personalized Approach", desc: "Every case is unique. We create tailored strategies that maximize your success chances." },
                { icon: Globe, title: "Global Network", desc: "Partnerships with institutions and employers across 25+ countries for comprehensive support." },
                { icon: Award, title: "Transparent Process", desc: "No hidden costs, no false promises. We believe in complete transparency at every step." }].
                map((item, index) =>
                <div key={index} className="flex gap-4 group">
                    <div className="w-11 h-11 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-500 transition-colors duration-300">
                      <item.icon className="w-5 h-5 text-brand-500 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-base mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-brand-100/40">
                <img
                  src="https://images.unsplash.com/photo-1553697388-94e804e2f0f6?w=600&q=80"
                  alt="Immigration Consultancy"
                  className="w-full h-[520px] object-cover" />

              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-5 border border-brand-100/60">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {["NR", "PR"].map((initials, i) =>
                    <div key={i} className="w-9 h-9 rounded-full bg-brand-100 border-2 border-white flex items-center justify-center">
                        <span className="text-[10px] font-bold text-brand-500">{initials}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Expert Team</p>
                    <p className="text-xs text-gray-500">3+ years combined</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-brand-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Testimonials
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-500 text-lg">
              Real stories from real people whose lives we've helped transform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial, index) =>
            <Card key={index} className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-7">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  )}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-brand-500">{testimonial.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-xs text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>


      {/* Client Success Stories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Success Stories
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Clients, Our Pride
            </h2>
            <p className="text-gray-500 text-lg">
              Real people achieving their dreams of working abroad with NexFLY's expert guidance.
            </p>
          </div>

          <div className="flex justify-center gap-8 flex-wrap">
            <div className="w-full sm:w-[380px] group">
              <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_immigration-hub-6/artifacts/htweqziw_IMG_6932.jpeg"
                    alt="Mr. Nagaraj - Work Permit Czech Republic"
                    className="w-full h-[320px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-800/80 to-transparent p-5">
                    <Badge className="bg-brand-500 text-white text-xs font-semibold rounded-lg mb-2">Work Permit - Czech Republic</Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">Mr. Nagaraj</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Mr. Nagaraj receives a work permit for the Czech Republic. Another success story powered by NexFLY's dedicated work permit services.
                  </p>
                </CardContent>
              </Card>
            </div>
            <div className="w-full sm:w-[380px] group">
              <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_immigration-hub-6/artifacts/uc8lc7cb_IMG_6920.jpeg"
                    alt="Mr. Sridhar - Work Permit Czech Republic"
                    className="w-full h-[320px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-800/80 to-transparent p-5">
                    <Badge className="bg-brand-500 text-white text-xs font-semibold rounded-lg mb-2">Work Permit - Czech Republic</Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">Mr. Sridhar</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Mr. Sridhar receives a work permit for the Czech Republic. NexFLY's expert guidance made his international career dreams a reality.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-800 rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-700/30 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-900/30 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-brand-200 text-lg mb-8 max-w-xl mx-auto">
                Book a free consultation with our immigration experts and take the first step towards your global dreams.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-xl h-12 px-8 text-sm font-semibold shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                    Book Free Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 rounded-xl h-12 px-8 text-sm font-semibold transition-all duration-300">
                    Know More About Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>);

}